/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {ChangeDetectorRef, Component, Input, OnChanges, OnInit} from '@angular/core';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';

import {merge} from 'rxjs';
import {debounceTime, distinctUntilChanged, takeUntil, tap} from 'rxjs/operators';

import {Destroyable} from '../../destroyable.component';
import {Report} from '../report';
import {DateOptions, ReportTimeDateOption, ReportTimeDateType} from '../reports.interfaces';
import {ReportsService} from '../reports.service';

@Component({
  selector: 'gp-report-time',
  templateUrl: './report-time.component.html',
  styleUrls: ['./report-time.component.scss']
})

export class ReportTimeComponent extends Destroyable implements OnChanges, OnInit {
  @Input() public report: Report;
  public singleDateOptions: DateOptions[];
  public betweenDateOptions: DateOptions[];

  public selectedDateType: string = ReportTimeDateType.SINGLE_DATE;

  public beginDateControl: UntypedFormControl = new UntypedFormControl();
  public endDateControl: UntypedFormControl = new UntypedFormControl();

  public readonly ReportTimeDateType = ReportTimeDateType;
  public isDateReadonly = false;

  singleDateForm: UntypedFormGroup;
  betweenDatesForm: UntypedFormGroup;

  private customBeginDate = '';
  private customEndDate = '';
  private isExistingReport = false;

  constructor(private route: ActivatedRoute,
              private reportsService: ReportsService,
              private cd: ChangeDetectorRef,
              private fb: UntypedFormBuilder) {
    super();
    this.singleDateOptions = this.reportsService.singleDateOptions;
    this.betweenDateOptions = this.reportsService.betweenDateOptions;
  }

  public get isDateTypeSingle(): boolean {
    return this.selectedDateType === ReportTimeDateType.SINGLE_DATE;
  }

  private renderNewReportUI(): void {
    this.selectedDateType = ReportTimeDateType.SINGLE_DATE;
    this.isDateReadonly = true;

    const defaultSingleDateItem = this.singleDateOptions.filter(item => !!item.isDefault)[0];
    this.updateData(defaultSingleDateItem);
  }

  private renderExistingReport() {
    if (this.report.reportEntity.beginDate === this.report.reportEntity.endDate) {
      this.selectedDateType = ReportTimeDateType.SINGLE_DATE;
    } else {
      this.selectedDateType = ReportTimeDateType.BETWEEN_DATES;
    }

    if (!this.report.reportEntity?.dateOption) {
      this.report.reportEntity.dateOption = ReportTimeDateOption.CUSTOM;
    }

    if (this.report.reportEntity.dateOption === ReportTimeDateOption.CUSTOM) {
      this.customBeginDate = this.report.reportEntity.beginDate;
      this.customEndDate = this.report.reportEntity.endDate;
    }

    this.updateFormValues({
      id: this.report.reportEntity.dateOption,
      beginDate: this.report.reportEntity.beginDate,
      endDate: this.report.reportEntity.endDate,
    });
  }

  private setDateAndOptions(id: string, dateOptions: DateOptions[]): void {
    if (id === ReportTimeDateOption.CUSTOM) {
      this.isDateReadonly = false;
      if (this.isExistingReport) {
        this.setFormValues(this.customBeginDate, this.customEndDate);
      } else {
        this.setFormValues();
      }

      this.updatePayload({
        id: id,
        beginDate: this.customBeginDate,
        endDate: this.customEndDate
      });
    } else {
      this.isDateReadonly = true;

      const selectedItem = dateOptions.filter(item => item.id === id)[0];
      this.updatePayload(selectedItem);

      this.setFormValues(selectedItem.beginDate, selectedItem.endDate);
    }

    this.reportsService.$reportUpdated.next(this.report);
  }

  private setFormValues(beginDate = '', endDate = ''): void {
    if (this.isDateTypeSingle) {
      this.singleDateForm.get('beginDateControl').setValue(beginDate);
    } else {
      this.betweenDatesForm.get('beginDateControl').setValue(beginDate);
      this.betweenDatesForm.get('endDateControl').setValue(endDate);
    }

    this.cd.detectChanges();
  }

  private updateData(item: DateOptions) {
    this.updatePayload(item);
    this.updateFormValues(item);
  }

  private updatePayload(item: DateOptions) {
    this.report.reportEntity.dateOption = item?.id;
    this.report.reportEntity.beginDate = item?.beginDate;

    this.report.reportEntity.endDate = (this.isDateTypeSingle) ?
      item?.beginDate : item?.endDate;
  }

  private updateFormValues(item: DateOptions) {
    setTimeout(() => {
      if (this.isDateTypeSingle) {
        this.singleDateForm.setValue({
          singleDateOptionCtrl: item?.id,
          beginDateControl: item?.beginDate
        });
      } else {
        this.betweenDatesForm.setValue({
          betweenDatesOptionCtrl: item?.id,
          beginDateControl: item?.beginDate,
          endDateControl: item?.endDate
        });
      }
    }, 0);
  }

  public ngOnInit(): void {
    this.route.params.subscribe((params: any) => {
      this.isExistingReport = !!params?.id;
    });

    this.singleDateForm = this.fb.group({
      singleDateOptionCtrl: [''],
      beginDateControl: [''],
    });

    this.betweenDatesForm = this.fb.group({
      betweenDatesOptionCtrl: [''],
      beginDateControl: [''],
      endDateControl: ['']
    });

    this.singleDateForm.get('singleDateOptionCtrl').valueChanges
      .pipe(
        debounceTime(100),
        tap(id => this.selectSingleDate(id)),
        takeUntil(this.componentDestroyed$)
      ).subscribe();

    this.betweenDatesForm.get('betweenDatesOptionCtrl').valueChanges
      .pipe(
        debounceTime(100),
        tap(id => this.selectBetweenDates(id)),
        takeUntil(this.componentDestroyed$)
      ).subscribe();

    merge(
      this.singleDateForm.get('beginDateControl').valueChanges.pipe(distinctUntilChanged()),
      this.betweenDatesForm.get('beginDateControl').valueChanges.pipe(distinctUntilChanged()),
    ).pipe(
      debounceTime(650),
      tap(beginDate => {
        if(this.isExistingReport) {
          const currentEndDate = this.isDateTypeSingle ? beginDate : this.report.reportEntity.endDate;
          if (beginDate !== this.report.reportEntity.beginDate || currentEndDate !== this.report.reportEntity.endDate) {
            this.reportsService.isChanged = true;
          }
        }
        if (this.isDateTypeSingle) {
          this.report.reportEntity.beginDate = beginDate;
          this.report.reportEntity.endDate = beginDate;
        } else {
          this.report.reportEntity.beginDate = beginDate;
        }
        this.reportsService.$reportUpdated.next(this.report);
        this.cd.markForCheck();
      }),
      takeUntil(this.componentDestroyed$)
    ).subscribe();

    this.betweenDatesForm.get('endDateControl').valueChanges
      .pipe(
        debounceTime(650),
        distinctUntilChanged(),
        tap(endDate => {
          if(this.isExistingReport){
            if (endDate !== this.report.reportEntity.endDate) {
              this.reportsService.isChanged = true;
            }
          }
          this.report.reportEntity.endDate = endDate;
          this.reportsService.$reportUpdated.next(this.report);
          this.cd.markForCheck();
        }),
        takeUntil(this.componentDestroyed$)
      ).subscribe();
  }

  public ngOnChanges(): void {
    if (this.report) {
      if (this.isExistingReport) {
        this.renderExistingReport();
      } else {
        this.renderNewReportUI();
      }
    }
  }

  public selectSingleDate(id: string): void {
    this.setDateAndOptions(id, [...this.singleDateOptions]);
  }

  public selectBetweenDates(id: string): void {
    this.setDateAndOptions(id, [...this.betweenDateOptions]);
  }

  public onChangeType(type: string): void {
    this.selectedDateType = type;
    this.isDateReadonly = true;

    const dateOptions = this.isDateTypeSingle ? this.singleDateOptions : this.betweenDateOptions;
    const defaultDateItem = dateOptions.filter(item => !!item.isDefault)[0];
    this.updateData(defaultDateItem);
    this.reportsService.$reportTimeTypeChanged.next(type);
    this.reportsService.$reportUpdated.next(this.report);
    if( this.isExistingReport) {
      this.reportsService.isChanged = true;
    }
  }

  onDateChange() {
    this.reportsService.$reportUpdated.next(this.report);
    if(this.isExistingReport) {
      this.reportsService.isChanged = true;
    }
  }
}
